package org.lennycodes.gradematewebsite.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.icons.fa.IconCategory
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.AnimationDirection
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.lennycodes.gradematewebsite.components.style.DesktopNavLinksStyle
import org.lennycodes.gradematewebsite.components.style.GradeMateColors
import org.lennycodes.gradematewebsite.components.style.NavLinkStyle
import org.lennycodes.gradematewebsite.components.style.PrimaryButtonStyle
import org.lennycodes.gradematewebsite.components.style.WobbleKeyframes
import org.lennycodes.gradematewebsite.components.style.toRgba
import org.w3c.dom.events.Event

private val NavItems = listOf(
    "home" to "Home",
    "docs" to "Documentation",
    "downloads" to "Downloads",
    "contact" to "Contact"
)

private const val Ease = "cubic-bezier(0.22, 1, 0.36, 1)"
private const val EaseOutBack = "cubic-bezier(0.34, 1.56, 0.64, 1)"

private const val BAR_HEIGHT = 72.0
private const val BAR_PADDING_X = 32

@Composable
fun NavBar() {
    var menuOpen by remember { mutableStateOf(false) }
    var scrolled by remember { mutableStateOf(false) }
    var progress by remember { mutableStateOf(0.0) }
    var activeSection by remember { mutableStateOf("home") }
    var hoveredLink by remember { mutableStateOf<String?>(null) }
    var brandHovered by remember { mutableStateOf(false) }
    var ctaHovered by remember { mutableStateOf(false) }
    var ctaPressed by remember { mutableStateOf(false) }
    var mounted by remember { mutableStateOf(false) }

    val reduceMotion = remember { window.matchMedia("(prefers-reduced-motion: reduce)").matches }
    fun t(ms: Int) = if (reduceMotion) 0 else ms

    LaunchedEffect(Unit) {
        delay(60)
        mounted = true
    }

    // Scroll state + scroll-spy
    DisposableEffect(Unit) {
        val update = {
            val y = window.scrollY
            scrolled = y > 8

            val max = (document.documentElement?.scrollHeight ?: 0) - window.innerHeight
            progress = if (max > 0) (y / max).coerceIn(0.0, 1.0) else 0.0

            val threshold = BAR_HEIGHT + 8

            if (max > 0 && y >= max - 4) {
                activeSection = NavItems.last().first
            } else {
                activeSection = NavItems
                    .map { it.first }
                    .lastOrNull { id ->
                        val el = document.getElementById(id) ?: return@lastOrNull false
                        val rect = el.getBoundingClientRect()
                        rect.top <= threshold && rect.bottom > threshold
                    }
                    ?: NavItems
                        .map { it.first }
                        .lastOrNull { id ->
                            (document.getElementById(id)?.getBoundingClientRect()?.top
                                ?: Double.MAX_VALUE) <= threshold
                        }
                            ?: "home"
            }
        }
        val onScroll: (Event) -> Unit = { update() }
        val onResize: (Event) -> Unit = { update() }
        window.addEventListener("scroll", onScroll)
        window.addEventListener("resize", onResize)
        update()
        onDispose {
            window.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", onResize)
        }
    }

    Column(
        Modifier
            .fillMaxWidth()
            .position(Position.Sticky)
            .top(0.px)
            .zIndex(50)
            .backgroundColor(GradeMateColors.Surface)
            .styleModifier {
                // Layered glass — clear at top, frosted once scrolled
                property(
                    "background-color",
                    GradeMateColors.Surface.toRgba(alpha = if (scrolled) 0.82f else 0.55f)
                )
                val blur = if (scrolled) 22 else 10
                val sat = if (scrolled) 200 else 160
                property("backdrop-filter", "saturate(${sat}%) blur(${blur}px)")
                property("-webkit-backdrop-filter", "saturate(${sat}%) blur(${blur}px)")

                // Top-lit gradient sheen when compact
                property(
                    "background-image",
                    if (scrolled)
                        "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(255,255,255,0) 100%)"
                    else "none"
                )

                property(
                    "border-bottom",
                    "1px solid ${if (scrolled) GradeMateColors.Border35 else "transparent"}"
                )
                property(
                    "box-shadow",
                    if (scrolled) "0 16px 44px -20px rgba(15, 23, 42, 0.18)"
                    else "0 0 0 rgba(15, 23, 42, 0)"
                )
                property("transform", if (mounted) "translateY(0)" else "translateY(-100%)")
                property("opacity", if (mounted) "1" else "0")
                property(
                    "transition",
                    "background-color ${t(400)}ms ease, " +
                            "backdrop-filter ${t(400)}ms ease, " +
                            "border-color ${t(400)}ms ease, " +
                            "box-shadow ${t(400)}ms ease, " +
                            "transform ${t(800)}ms $Ease, " +
                            "opacity ${t(500)}ms ease"
                )
            }
    ) {

        Box(
            Modifier
                .fillMaxWidth()
                .styleModifier { property("position", "relative") }
        ) {

            Row(
                Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        val padY = if (scrolled) 11 else 15
                        property("padding", "${padY}px ${BAR_PADDING_X}px")
                        property("transition", "padding ${t(400)}ms $Ease")
                    },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {

                // ---------------------------------------------------------
                // BRAND
                // ---------------------------------------------------------
                Row(
                    Modifier
                        .onClick { window.location.hash = "home" }
                        .onMouseEnter { brandHovered = true }
                        .onMouseLeave { brandHovered = false }
                        .styleModifier {
                            property("display", "inline-flex")
                            property("align-items", "center")
                            property("cursor", "pointer")
                            property("text-decoration", "none")
                            property("flex-shrink", "0")
                            property("gap", "12px")
                        }
                ) {
                    // Logo tile — organic radius shift, glows on hover
                    Box(
                        Modifier
                            .borderRadius(13.px)
                            .backgroundColor(GradeMateColors.Primary)
                            .styleModifier {
                                val side = if (scrolled) 38 else 42
                                property("width", "${side}px")
                                property("height", "${side}px")
                                property(
                                    "box-shadow",
                                    if (brandHovered)
                                        "0 16px 32px -10px rgba(37, 99, 235, 0.5), " +
                                                "inset 0 1px 0 rgba(255, 255, 255, 0.25)"
                                    else
                                        "0 8px 20px -8px rgba(37, 99, 235, 0.35), " +
                                                "inset 0 1px 0 rgba(255, 255, 255, 0.18)"
                                )
                                property(
                                    "transform",
                                    if (brandHovered) "rotate(-6deg) scale(1.08)" else "rotate(0deg) scale(1)"
                                )
                                property("overflow", "hidden")
                                property("flex-shrink", "0")
                                property(
                                    "transition",
                                    "width ${t(400)}ms $Ease, height ${t(400)}ms $Ease, " +
                                            "transform ${t(450)}ms $EaseOutBack, " +
                                            "box-shadow ${t(300)}ms ease"
                                )
                            }
                    ) {
                        Img(
                            src = "/grademate.png",
                            attrs = Modifier
                                .styleModifier {
                                    property("width", "100%")
                                    property("height", "100%")
                                    property("display", "block")
                                    property("object-fit", "cover")
                                }
                                .toAttrs()
                        )
                    }

                    Column(Modifier.margin(left = 0.px)) {
                        P(
                            Modifier
                                .margin(0.px)
                                .fontSize(18.px)
                                .fontWeight(FontWeight.Bold)
                                .color(GradeMateColors.Primary)
                                .styleModifier {
                                    property("letter-spacing", "-0.03em")
                                    property("line-height", "1.05")
                                    property("white-space", "nowrap")
                                }
                                .toAttrs()
                        ) {
                            Text("GradeMate")
                        }

                        // Tagline — a hallmark, with tighter type and less noise
                        P(
                            Modifier
                                .fontSize(9.5.px)
                                .fontWeight(FontWeight.Bold)
                                .styleModifier {
                                    property("margin", "0")
                                    property("margin-top", if (scrolled) "0" else "4px")
                                    property("max-height", if (scrolled) "0" else "12px")
                                    property("opacity", if (scrolled) "0" else "0.6")
                                    property("color", GradeMateColors.TextSecondary.toString())
                                    property("overflow", "hidden")
                                    property("letter-spacing", "0.22em")
                                    property("line-height", "1")
                                    property("white-space", "nowrap")
                                    property(
                                        "transition",
                                        "max-height ${t(400)}ms $Ease, margin-top ${t(400)}ms $Ease, opacity ${t(250)}ms ease"
                                    )
                                }
                                .toAttrs()
                        ) {
                            Text("SMARTER GRADING")
                        }
                    }
                }

                // ---------------------------------------------------------
                // DESKTOP NAVIGATION
                // ---------------------------------------------------------
                Row(
                    DesktopNavLinksStyle.toModifier(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.px)
                ) {

                    NavItems.forEachIndexed { index, (id, label) ->
                        val isActive = activeSection == id
                        val isHovered = hoveredLink == id

                        Box(
                            Modifier
                                .onMouseEnter { hoveredLink = id }
                                .onMouseLeave { hoveredLink = null }
                                .styleModifier {
                                    property("position", "relative")
                                    property("opacity", if (mounted) "1" else "0")
                                    property("transform", if (mounted) "translateY(0)" else "translateY(-10px)")
                                    property(
                                        "transition",
                                        "opacity ${t(500)}ms ease ${t(250 + index * 70)}ms, " +
                                                "transform ${t(600)}ms $Ease ${t(250 + index * 70)}ms"
                                    )
                                }
                        ) {
                            Link(
                                "#$id",
                                label,
                                NavLinkStyle.toModifier()
                                    .thenIf(isActive) { Modifier.color(GradeMateColors.Primary) }
                                    .styleModifier {
                                        property(
                                            "transition",
                                            "color ${t(250)}ms ease"
                                        )
                                    }
                            )

                            // Underline — soft, sits clear of the text
                            Box(
                                Modifier.styleModifier {
                                    property("position", "absolute")
                                    property("left", "14px")
                                    property("right", "14px")
                                    property("bottom", "-5px")
                                    property("height", "2px")
                                    property("border-radius", "2px")
                                    property(
                                        "background-image",
                                        "linear-gradient(90deg, ${GradeMateColors.Primary}, ${GradeMateColors.Primary.toRgba(alpha = 0.6f)})"
                                    )
                                    property("pointer-events", "none")
                                    property("transform-origin", "center")
                                    property(
                                        "transform",
                                        when {
                                            isActive -> "scaleX(1)"
                                            isHovered -> "scaleX(0.5)"
                                            else -> "scaleX(0)"
                                        }
                                    )
                                    property("opacity", if (isActive) "1" else if (isHovered) "0.6" else "0")
                                    property(
                                        "transition",
                                        "transform ${t(350)}ms $Ease, opacity ${t(250)}ms ease"
                                    )
                                }
                            )
                        }
                    }

                    // -----------------------------------------------------
                    // CTA — the crown jewel
                    // -----------------------------------------------------
                    Box(
                        Modifier
                            .margin(left = 14.px)
                            .styleModifier {
                                property("opacity", if (mounted) "1" else "0")
                                property("transform", if (mounted) "translateY(0)" else "translateY(-10px)")
                                property(
                                    "transition",
                                    "opacity ${t(500)}ms ease ${t(250 + NavItems.size * 70)}ms, " +
                                            "transform ${t(600)}ms $Ease ${t(250 + NavItems.size * 70)}ms"
                                )
                            }
                    ) {
                        Link(
                            path = "#downloads",
                            text = "Get GradeMate",
                            modifier = PrimaryButtonStyle
                                .toModifier()
                                .fillMaxWidth()
                                .margin(top = 8.px)
                                .padding(topBottom = 13.px, leftRight = 16.px)
                                .borderRadius(11.px)
                                .fontSize(14.px)
                                .fontWeight(FontWeight.SemiBold)
                                .color(Colors.White)
                                .onClick { menuOpen = false }
                                .animation(
                                    WobbleKeyframes.toAnimation(
                                        duration = 1.s,
                                        iterationCount = AnimationIterationCount.Infinite,
                                        timingFunction = AnimationTimingFunction.EaseInOut,
                                        direction = AnimationDirection.Alternate,
                                    )
                                )
                                .styleModifier {
                                    property("display", "inline-flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                    property("text-decoration", "none")

                                    property(
                                        "background-image",
                                        "linear-gradient(135deg, " +
                                                "rgba(255, 255, 255, 0.22), " +
                                                "rgba(255, 255, 255, 0) 60%)"
                                    )

                                    property(
                                        "box-shadow",
                                        "0 12px 24px -12px rgba(37, 99, 235, 0.5), " +
                                                "inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                    )
                                }
                        )
                    }
                }

                // ---------------------------------------------------------
                // MOBILE MENU BUTTON
                // ---------------------------------------------------------
                Button(
                    attrs = Modifier
                        .size(42.px)
                        .borderRadius(12.px)
                        .onClick { menuOpen = !menuOpen }
                        .styleModifier {
                            property("position", "relative")
                            property("padding", "0")
                            property(
                                "background-color",
                                if (menuOpen) GradeMateColors.Primary.toString()
                                else GradeMateColors.Surface.toRgba(alpha = 0.9f)
                            )
                            property(
                                "border",
                                "1px solid ${if (menuOpen) GradeMateColors.Primary.toRgba() else GradeMateColors.Border35}"
                            )
                            property(
                                "box-shadow",
                                if (menuOpen) "0 12px 24px -10px rgba(37, 99, 235, 0.5)"
                                else "0 4px 10px -4px rgba(15, 23, 42, 0.08)"
                            )
                            property("cursor", "pointer")
                            property("flex-shrink", "0")
                            property(
                                "transition",
                                "background-color ${t(250)}ms ease, " +
                                        "border-color ${t(250)}ms ease, " +
                                        "box-shadow ${t(280)}ms ease"
                            )
                        }
                        .toAttrs {
                            attr("type", "button")
                            attr("aria-label", if (menuOpen) "Close menu" else "Open menu")
                            attr("aria-expanded", menuOpen.toString())
                        }
                ) {
                    listOf(-6, 0, 6).forEachIndexed { index, offset ->
                        Span(
                            Modifier
                                .width(18.px)
                                .height(1.6.px)
                                .borderRadius(2.px)
                                .styleModifier {
                                    property("position", "absolute")
                                    property("top", "50%")
                                    property("left", "50%")
                                    property(
                                        "background-color",
                                        if (menuOpen) Colors.White.toString()
                                        else GradeMateColors.TextPrimary.toString()
                                    )
                                    property(
                                        "transform",
                                        when {
                                            !menuOpen -> "translate(-50%, calc(-50% + ${offset}px))"
                                            index == 0 -> "translate(-50%, -50%) rotate(45deg)"
                                            index == 2 -> "translate(-50%, -50%) rotate(-45deg)"
                                            else -> "translate(-50%, -50%) scaleX(0)"
                                        }
                                    )
                                    property("opacity", if (menuOpen && index == 1) "0" else "1")
                                    property(
                                        "transition",
                                        "transform ${t(350)}ms $Ease, opacity ${t(200)}ms ease, background-color ${t(250)}ms ease"
                                    )
                                }.toAttrs()
                        ) {}
                    }
                }
            }

            // Reading progress hairline
            Box(
                Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("position", "absolute")
                        property("left", "0")
                        property("bottom", "-1px")
                        property("height", "2px")
                        property("pointer-events", "none")
                        property(
                            "background-image",
                            "linear-gradient(90deg, ${GradeMateColors.Primary}, ${GradeMateColors.Primary.toRgba(alpha = 0.4f)})"
                        )
                        property("transform-origin", "left")
                        property("transform", "scaleX($progress)")
                        property("opacity", if (scrolled) "1" else "0")
                        property("transition", "opacity ${t(300)}ms ease")
                    }
            )
        }

        // -------------------------------------------------------------
        // MOBILE DROPDOWN
        // -------------------------------------------------------------
        Column(
            Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("overflow-x", "hidden")
                    property("overflow-y", if (menuOpen) "auto" else "hidden")
                    property("max-height", if (menuOpen) "calc(100vh - ${BAR_HEIGHT.toInt()}px)" else "0")
                    property("opacity", if (menuOpen) "1" else "0")
                    property("visibility", if (menuOpen) "visible" else "hidden")
                    property(
                        "border-top",
                        if (menuOpen) "1px solid ${GradeMateColors.Border35}" else "0 solid transparent"
                    )
                    property(
                        "box-shadow",
                        if (menuOpen) "0 24px 48px -20px rgba(15, 23, 42, 0.22)" else "none"
                    )
                    property(
                        "transition",
                        "max-height ${t(450)}ms $Ease, opacity ${t(300)}ms ease, " +
                                "visibility 0s linear ${if (menuOpen) 0 else t(450)}ms"
                    )
                }
        ) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 24.px, topBottom = 20.px),
                verticalArrangement = Arrangement.spacedBy(6.px)
            ) {

                NavItems.forEachIndexed { index, (id, label) ->
                    val isActive = activeSection == id
                    val delay = if (menuOpen) t(90 + index * 55) else 0

                    Link(
                        "#$id",
                        label,
                        NavLinkStyle
                            .toModifier()
                            .fillMaxWidth()
                            .padding(topBottom = 13.px, leftRight = 14.px)
                            .borderRadius(11.px)
                            .onClick { menuOpen = false }
                            .styleModifier {
                                property("opacity", if (menuOpen) "1" else "0")
                                property("transform", if (menuOpen) "translateY(0)" else "translateY(-10px)")
                                if (isActive) {
                                    property("background-color", GradeMateColors.Primary.toRgba(alpha = 0.08f))
                                    property("color", GradeMateColors.Primary.toString())
                                }
                                property(
                                    "transition",
                                    "opacity ${t(300)}ms ease ${delay}ms, " +
                                            "transform ${t(450)}ms $Ease ${delay}ms, " +
                                            "background-color 200ms ease, color 200ms ease"
                                )
                            }
                    )
                }

                val ctaDelay = if (menuOpen) t(90 + NavItems.size * 55) else 0
                Box(
                    Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("opacity", if (menuOpen) "1" else "0")
                            property(
                                "transform",
                                if (menuOpen) "translateY(0)" else "translateY(-10px)"
                            )
                            property(
                                "transition",
                                "opacity ${t(300)}ms ease ${ctaDelay}ms, " +
                                        "transform ${t(450)}ms $Ease ${ctaDelay}ms"
                            )
                        }
                ) {
                    Link(
                        path = "#downloads",
                        text = "Get GradeMate",
                        modifier = PrimaryButtonStyle
                            .toModifier()
                            .fillMaxWidth()
                            .onMouseEnter { ctaHovered = true }
                            .onMouseLeave {
                                ctaHovered = false
                                ctaPressed = false
                            }
                            .styleModifier {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("justify-content", "center")   // ← centers the text horizontally
                                property("gap", "8px")
                                property("height", "42px")
                                property("box-sizing", "border-box")
                                property("padding", "0 22px")
                                property("border-radius", "11px")
                                property("text-decoration", "none")
                                property("font-size", "14px")
                                property("font-weight", "650")
                                property("letter-spacing", "-0.01em")
                                property("line-height", "1")
                                property("color", Colors.White.toString())
                                property("width", "100%")               // ← ensures fillMaxWidth on flex children

                                // Sheen + top highlight
                                property(
                                    "background-image",
                                    "linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0) 60%)"
                                )
                                property(
                                    "box-shadow",
                                    when {
                                        ctaPressed && ctaHovered ->
                                            "0 10px 22px -8px rgba(37, 99, 235, 0.4), " +
                                                    "inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                        ctaHovered ->
                                            "0 20px 38px -10px rgba(37, 99, 235, 0.55), " +
                                                    "inset 0 1px 0 rgba(255, 255, 255, 0.28)"
                                        else ->
                                            "0 8px 18px -8px rgba(37, 99, 235, 0.4), " +
                                                    "inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                    }
                                )
                                property(
                                    "transform",
                                    when {
                                        ctaPressed && ctaHovered -> "translateY(-1px)"
                                        ctaHovered -> "translateY(-2px)"
                                        else -> "translateY(0)"
                                    }
                                )
                                property(
                                    "transition",
                                    "transform ${t(220)}ms $Ease, box-shadow ${t(250)}ms ease"
                                )
                            }
                    )
                }
            }
        }
    }
}